import React, { useEffect, useState } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import Input from '../../../components/ui/Input';

const UserDetailsDrawer = ({
  user,
  isOpen,
  onClose,
  onSave,
  roleOptions = [],
  users = [],
  departmentOptions: propDepartmentOptions = [],
}) => {
  const [isSubmitting, setIsSubmitting] = useState(false);

  const [formData, setFormData] = useState({
    name: '',
    email: '',
    password: '',
    password_confirmation: '',
    employeeId: '',
    department: 'IT',
    role_id: '',
    status: 'Active',
    phoneNumber: '',
    location: '',
    manager: '',
    title: '',
  });

  const [formErrors, setFormErrors] = useState({});

  const fallbackDepartmentOptions = [
    {
      value: 'IT',
      label: 'IT',
      color: 'text-blue-500',
      bg: 'bg-blue-50',
    },
    {
      value: 'Finance',
      label: 'Finance',
      color: 'text-green-500',
      bg: 'bg-green-50',
    },
    {
      value: 'HR',
      label: 'HR',
      color: 'text-purple-500',
      bg: 'bg-purple-50',
    },
    {
      value: 'Operations',
      label: 'Operations',
      color: 'text-orange-500',
      bg: 'bg-orange-50',
    },
    {
      value: 'Marketing',
      label: 'Marketing',
      color: 'text-pink-500',
      bg: 'bg-pink-50',
    },
    {
      value: 'Sales',
      label: 'Sales',
      color: 'text-red-500',
      bg: 'bg-red-50',
    },
  ];

  const departmentOptions =
    propDepartmentOptions && propDepartmentOptions.length > 0
      ? propDepartmentOptions
      : fallbackDepartmentOptions;

  const statusOptions = [
    {
      value: 'Active',
      label: 'Active',
      color: 'success',
      icon: 'UserCheck',
    },
    {
      value: 'Pending',
      label: 'Pending',
      color: 'warning',
      icon: 'Clock',
    },
    {
      value: 'Suspended',
      label: 'Suspended',
      color: 'error',
      icon: 'UserX',
    },
  ];


  const isReportingManager = (candidate) => {
    const roleName =
      candidate?.role?.name ||
      candidate?.role_name ||
      candidate?.roleName ||
      '';

    return /manager|admin/i.test(String(roleName));
  };

  const reportingManagerOptions = users
    .filter((candidate) => {
      if (!isReportingManager(candidate)) return false;
      if (user?.id && Number(candidate?.id) === Number(user.id)) return false;
      return true;
    })
    .sort((a, b) =>
      String(a?.name || '').localeCompare(String(b?.name || ''))
    );

  const handleGetCurrentLocation = () => {
    if (!navigator.geolocation) {
      alert('Geolocation is not supported by this browser. Please type the location manually.');
      return;
    }

    navigator.geolocation.getCurrentPosition(
      async (position) => {
        const { latitude, longitude } = position.coords;

        try {
          const response = await fetch(
            `https://api.bigdatacloud.net/data/reverse-geocode-client?latitude=${latitude}&longitude=${longitude}&localityLanguage=en`
          );

          if (!response.ok) {
            throw new Error('Unable to determine your location.');
          }

          const data = await response.json();
          const locationParts = [
            data?.city,
            data?.locality,
            data?.principalSubdivision,
            data?.countryName,
          ].filter(Boolean);

          const uniqueParts = [...new Set(locationParts)];
          const readableLocation = uniqueParts.length
            ? uniqueParts.join(', ')
            : `${latitude.toFixed(6)}, ${longitude.toFixed(6)}`;

          handleInputChange('location', readableLocation);
        } catch (error) {
          console.error('Reverse geocoding failed:', error);
          handleInputChange(
            'location',
            `${latitude.toFixed(6)}, ${longitude.toFixed(6)}`
          );
          alert('Could not get the address automatically. Coordinates were added instead.');
        }
      },
      (error) => {
        console.error('Geolocation error:', error);

        if (error.code === error.PERMISSION_DENIED) {
          alert('Location permission was denied. Please allow location access or type the location manually.');
        } else if (error.code === error.TIMEOUT) {
          alert('Location request timed out. Please try again or type the location manually.');
        } else {
          alert('Unable to get your current location. Please type the location manually.');
        }
      },
      {
        enableHighAccuracy: true,
        timeout: 15000,
        maximumAge: 0,
      }
    );
  };

  /*
  |--------------------------------------------------------------------------
  | Populate Edit Form
  |--------------------------------------------------------------------------
  */

  useEffect(() => {
    if (!user || !isOpen) {
      return;
    }

    setFormData({
      name: user?.name || '',
      email: user?.email || '',

      /*
       * Never put the existing hashed password here.
       * Password fields stay empty for security.
       */
      password: '',
      password_confirmation: '',

      employeeId: user?.employeeId || user?.employee_id || '',
      department: user?.department || 'IT',
      role_id: user?.role_id || '',
      status: user?.status || 'Active',
      phoneNumber: user?.phoneNumber || user?.phone_number || '',
      location: user?.location || '',
      manager: user?.manager || '',
      title: user?.title || '',
    });

    setFormErrors({});
  }, [user, isOpen]);

  /*
  |--------------------------------------------------------------------------
  | Handle Input
  |--------------------------------------------------------------------------
  */

  const handleInputChange = (field, value) => {
    setFormData((previous) => ({
      ...previous,
      [field]: value,
    }));

    setFormErrors((previous) => ({
      ...previous,
      [field]: '',
    }));
  };

  /*
  |--------------------------------------------------------------------------
  | Validate Edit Form
  |--------------------------------------------------------------------------
  */

  const validateForm = () => {
    const errors = {};

    if (!formData.name.trim()) {
      errors.name = 'Name is required';
    }

    if (!formData.email.trim()) {
      errors.email = 'Email is required';
    } else if (!/\S+@\S+\.\S+/.test(formData.email)) {
      errors.email = 'Email is invalid';
    }

    /*
     * Password is optional during edit.
     *
     * If password is entered, confirmation is required.
     */

    if (formData.password) {
      if (formData.password.length < 8) {
        errors.password = 'Password must be at least 8 characters';
      }

      if (!formData.password_confirmation) {
        errors.password_confirmation = 'Please confirm your password';
      } else if (formData.password !== formData.password_confirmation) {
        errors.password_confirmation = 'Passwords do not match';
      }
    }

    setFormErrors(errors);

    return Object.keys(errors).length === 0;
  };

  /*
  |--------------------------------------------------------------------------
  | Save User
  |--------------------------------------------------------------------------
  */

  const handleSave = async () => {
    if (!validateForm()) {
      return;
    }

    try {
      setIsSubmitting(true);

      const dataToSave = {
        ...formData,
        id: user?.id,
      };

      /*
       * If password is empty, don't send password fields.
       * This keeps the existing password unchanged.
       */

      if (!dataToSave.password && !dataToSave.password_confirmation) {
        delete dataToSave.password;
        delete dataToSave.password_confirmation;
      }

      await onSave(dataToSave);

      setFormErrors({});

      // Clear password fields after success
      setFormData((prev) => ({
        ...prev,
        password: '',
        password_confirmation: '',
      }));

      // ✅ Native browser success alert
      alert('User updated successfully!');
    } catch (error) {
      console.error('Failed to update user:', error);

      // ✅ Native browser error alert
      alert(error?.message || 'Failed to update user. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  /*
  |--------------------------------------------------------------------------
  | Cancel
  |--------------------------------------------------------------------------
  */

  const handleCancel = () => {
    setFormErrors({});
    onClose();
  };

  /*
  |--------------------------------------------------------------------------
  | Close
  |--------------------------------------------------------------------------
  */

  const handleClose = () => {
    if (isSubmitting) {
      return;
    }

    setFormErrors({});
    onClose();
  };

  if (!isOpen || !user) {
    return null;
  }

  return (
    <>
      {/* =====================================================
          BACKDROP
      ====================================================== */}

      <div
        className="
          fixed
          inset-0
          bg-black/50
          backdrop-blur-sm
          z-40
          transition-opacity
        "
        onClick={handleClose}
      />

      {/* =====================================================
          EDIT USER DRAWER
      ====================================================== */}

      <div
        className="
          fixed
          right-0
          top-0
          h-full
          w-full
          max-w-2xl
          bg-card
          shadow-2xl
          z-50
          transform
          transition-transform
          duration-300
          ease-in-out
          animate-slide-in-right
        "
      >
        {/* =================================================
            HEADER
        ================================================== */}

        <div
          className="
            flex
            items-center
            justify-between
            p-6
            border-b
            border-border
            bg-gradient-to-r
            from-primary/5
            to-transparent
          "
        >
          <div className="flex items-center space-x-3">
            <div
              className="
                w-10
                h-10
                bg-primary/10
                rounded-lg
                flex
                items-center
                justify-center
              "
            >
              <Icon name="User" size={20} className="text-primary" />
            </div>

            <div>
              <h3 className="text-xl font-semibold text-card-foreground">
                Edit User
              </h3>

              <p className="text-sm text-muted-foreground mt-1">
                Update user account and role-based permissions
              </p>
            </div>
          </div>

          <button
            type="button"
            onClick={handleClose}
            disabled={isSubmitting}
            className="
              text-muted-foreground
              hover:text-card-foreground
              transition-colors
              p-2
              hover:bg-muted
              rounded-lg
              disabled:opacity-50
            "
          >
            <Icon name="X" size={20} />
          </button>
        </div>

        {/* =================================================
            CONTENT
        ================================================== */}

        <div className="overflow-y-auto h-[calc(100%-140px)]">
          <div className="p-6 space-y-6">
            {/* =================================================
                BASIC INFORMATION
            ================================================== */}

            <div>
              <h4
                className="
                  text-sm
                  font-semibold
                  text-card-foreground
                  mb-4
                  flex
                  items-center
                "
              >
                <Icon name="User" size={16} className="mr-2 text-primary" />
                Basic Information
              </h4>

              <div className="grid grid-cols-1 gap-4">
                {/* Full Name */}

                <div>
                  <label
                    className="
                      block
                      text-sm
                      font-medium
                      text-card-foreground
                      mb-2
                    "
                  >
                    Full Name
                    <span className="text-error">*</span>
                  </label>

                  <Input
                    value={formData.name}
                    onChange={(e) => handleInputChange('name', e.target.value)}
                    placeholder="Enter full name"
                    error={formErrors.name}
                    className="w-full"
                  />
                </div>

                {/* Email */}

                <div>
                  <label
                    className="
                      block
                      text-sm
                      font-medium
                      text-card-foreground
                      mb-2
                    "
                  >
                    Email Address
                    <span className="text-error">*</span>
                  </label>

                  <Input
                    type="email"
                    value={formData.email}
                    onChange={(e) => handleInputChange('email', e.target.value)}
                    placeholder="user@company.com"
                    error={formErrors.email}
                    className="w-full"
                  />
                </div>

                {/* Password */}

                <div>
                  <label
                    className="
                      block
                      text-sm
                      font-medium
                      text-card-foreground
                      mb-2
                    "
                  >
                    Password
                  </label>

                  <Input
                    type="password"
                    value={formData.password}
                    onChange={(e) => handleInputChange('password', e.target.value)}
                    placeholder="Enter new password"
                    error={formErrors.password}
                    className="w-full"
                  />

                  <p className="mt-1 text-xs text-muted-foreground">
                    Leave blank to keep the current password.
                  </p>
                </div>

                {/* Confirm Password */}

                <div>
                  <label
                    className="
                      block
                      text-sm
                      font-medium
                      text-card-foreground
                      mb-2
                    "
                  >
                    Confirm Password
                  </label>

                  <Input
                    type="password"
                    value={formData.password_confirmation}
                    onChange={(e) =>
                      handleInputChange('password_confirmation', e.target.value)
                    }
                    placeholder="Confirm new password"
                    error={formErrors.password_confirmation}
                    className="w-full"
                  />
                </div>

                {/* Employee ID + Job Title */}

                <div className="grid grid-cols-2 gap-4">
                  {/* Employee ID */}

                  <div>
                    <label
                      className="
                        block
                        text-sm
                        font-medium
                        text-card-foreground
                        mb-2
                      "
                    >
                      Employee ID
                    </label>

                    <Input
                      value={formData.employeeId}
                      onChange={(e) => handleInputChange('employeeId', e.target.value)}
                      placeholder="EMP001"
                      error={formErrors.employeeId}
                    />
                  </div>

                  {/* Job Title */}

                  <div>
                    <label
                      className="
                        block
                        text-sm
                        font-medium
                        text-card-foreground
                        mb-2
                      "
                    >
                      Job Title
                    </label>

                    <Input
                      value={formData.title}
                      onChange={(e) => handleInputChange('title', e.target.value)}
                      placeholder="e.g., Senior Developer"
                    />
                  </div>
                </div>
              </div>
            </div>

            {/* =================================================
                ORGANIZATION
            ================================================== */}

            <div>
              <h4
                className="
                  text-sm
                  font-semibold
                  text-card-foreground
                  mb-4
                  flex
                  items-center
                "
              >
                <Icon name="Briefcase" size={16} className="mr-2 text-primary" />
                Organization
              </h4>

              <div className="space-y-4">
                {/* Department */}

                <div>
                  <label
                    className="
                      block
                      text-sm
                      font-medium
                      text-card-foreground
                      mb-2
                    "
                  >
                    Department
                  </label>

                  <div className="grid grid-cols-2 gap-2">
                    {departmentOptions.map((dept) => (
                      <button
                        key={dept.value}
                        type="button"
                        onClick={() => handleInputChange('department', dept.value)}
                        className={`
                          flex
                          items-center
                          space-x-2
                          px-3
                          py-2
                          rounded-lg
                          border
                          transition-all
                          ${
                            formData.department === dept.value
                              ? 'border-primary bg-primary/5 text-primary'
                              : 'border-border hover:border-primary/50 text-card-foreground'
                          }
                        `}
                      >
                        <Icon name="Building2" size={14} className={dept.color} />

                        <span className="text-sm">{dept.label}</span>
                      </button>
                    ))}
                  </div>
                </div>

                {/* Role */}

                <div>
                  <label
                    className="
                      block
                      text-sm
                      font-medium
                      text-card-foreground
                      mb-2
                    "
                  >
                    Role
                  </label>

                  <div className="space-y-2">
                    {roleOptions.map((role) => (
                      <button
                        key={role.value}
                        type="button"
                        onClick={() => handleInputChange('role_id', role.value)}
                        className={`
                          flex
                          items-center
                          justify-between
                          w-full
                          px-3
                          py-3
                          rounded-lg
                          border
                          transition-all
                          ${
                            Number(formData.role_id) === Number(role.value)
                              ? 'border-primary bg-primary/5'
                              : 'border-border hover:border-primary/50'
                          }
                        `}
                      >
                        <div className="flex items-center space-x-3">
                          <Icon
                            name={role.icon || 'Shield'}
                            size={18}
                            className={
                              Number(formData.role_id) === Number(role.value)
                                ? 'text-primary'
                                : 'text-muted-foreground'
                            }
                          />

                          <div className="text-left">
                            <div
                              className={`
                                text-sm
                                font-medium
                                ${
                                  Number(formData.role_id) === Number(role.value)
                                    ? 'text-primary'
                                    : 'text-card-foreground'
                                }
                              `}
                            >
                              {role.label}
                            </div>

                            <div className="text-xs text-muted-foreground">
                              {role.description}
                            </div>
                          </div>
                        </div>

                        {Number(formData.role_id) === Number(role.value) && (
                          <Icon name="Check" size={16} className="text-primary" />
                        )}
                      </button>
                    ))}
                  </div>
                </div>
              </div>
            </div>

            {/* =================================================
                CONTACT INFORMATION
            ================================================== */}

            <div>
              <h4
                className="
                  text-sm
                  font-semibold
                  text-card-foreground
                  mb-4
                  flex
                  items-center
                "
              >
                <Icon name="Phone" size={16} className="mr-2 text-primary" />
                Contact Information
              </h4>

              <div className="grid grid-cols-1 gap-4">
                {/* Phone */}

                <div>
                  <label
                    className="
                      block
                      text-sm
                      font-medium
                      text-card-foreground
                      mb-2
                    "
                  >
                    Phone Number
                  </label>

                  <Input
                    value={formData.phoneNumber}
                    onChange={(e) => handleInputChange('phoneNumber', e.target.value)}
                    placeholder="+1 (555) 000-0000"
                  />
                </div>
                {/* Location */}
                <div>
                  <label
                    className="
                      block
                      text-sm
                      font-medium
                      text-card-foreground
                      mb-2
                    "
                  >
                    Location
                  </label>

                  <div className="flex gap-2">
                    <Input
                      value={formData.location}
                      onChange={(e) => handleInputChange('location', e.target.value)}
                      placeholder="Type location or use current location"
                      className="flex-1"
                    />
                    <Button
                      type="button"
                      variant="outline"
                      onClick={handleGetCurrentLocation}
                      iconName="MapPin"
                      iconPosition="left"
                    >
                      Use Location
                    </Button>
                  </div>

                  <p className="mt-1 text-xs text-muted-foreground">
                    You can type any location manually or automatically fetch your current location.
                  </p>
                </div>

                {/* Manager */}

                <div>
                  <label
                    className="
                      block
                      text-sm
                      font-medium
                      text-card-foreground
                      mb-2
                    "
                  >
                    Reporting Manager
                  </label>
                  <select
                    value={formData.manager}
                    onChange={(e) => handleInputChange('manager', e.target.value)}
                    className="
                      w-full
                      px-3
                      py-2
                      bg-background
                      border
                      border-border
                      rounded-lg
                      text-card-foreground
                      focus:outline-none
                      focus:ring-2
                      focus:ring-primary/20
                      focus:border-primary
                      transition-smooth
                    "
                  >
                    <option value="">Select reporting manager</option>
                    {formData.manager &&
                      !reportingManagerOptions.some(
                        (manager) => String(manager?.name) === String(formData.manager)
                      ) && (
                        <option value={formData.manager}>
                          {formData.manager} (Current)
                        </option>
                      )}
                    {reportingManagerOptions.map((manager) => (
                      <option key={manager.id} value={manager.name}>
                        {manager.name} — {manager?.role?.name || manager?.role_name || 'Manager'}
                      </option>
                    ))}
                  </select>
                  <p className="mt-1 text-xs text-muted-foreground">
                    Only users with Manager/Admin roles are available as reporting managers.
                  </p>
                </div>
              </div>
            </div>

            {/* =================================================
                ACCOUNT STATUS
            ================================================== */}

            <div>
              <h4
                className="
                  text-sm
                  font-semibold
                  text-card-foreground
                  mb-4
                  flex
                  items-center
                "
              >
                <Icon name="Shield" size={16} className="mr-2 text-primary" />
                Account Status
              </h4>

              <div className="flex space-x-3">
                {statusOptions.map((status) => {
                  const selected = formData.status === status.value;

                  return (
                    <button
                      key={status.value}
                      type="button"
                      onClick={() => handleInputChange('status', status.value)}
                      className={`
                        flex-1
                        flex
                        items-center
                        justify-center
                        space-x-2
                        px-4
                        py-3
                        rounded-lg
                        border
                        transition-all
                        ${
                          selected
                            ? 'border-primary bg-primary/5 text-primary'
                            : 'border-border hover:border-primary/50 text-card-foreground'
                        }
                      `}
                    >
                      <Icon name={status.icon} size={16} />

                      <span className="text-sm font-medium">{status.label}</span>
                    </button>
                  );
                })}
              </div>
            </div>

            {/* =================================================
                NOTE
            ================================================== */}

            <div
              className="
                p-4
                bg-muted/20
                rounded-lg
                border
                border-border
              "
            >
              <div className="flex items-start space-x-3">
                <Icon
                  name="Info"
                  size={16}
                  className="text-muted-foreground mt-0.5"
                />

                <div className="flex-1">
                  <p className="text-sm text-muted-foreground">
                    <span className="font-medium text-card-foreground">Note:</span>{' '}
                    Leave the password fields empty if you do not want to change the
                    user's current password.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* =================================================
            FOOTER
        ================================================== */}

        <div
          className="
            absolute
            bottom-0
            left-0
            right-0
            p-6
            border-t
            border-border
            bg-muted/10
          "
        >
          <div className="flex items-center justify-end space-x-3">
            {/* Cancel */}

            <Button variant="ghost" onClick={handleCancel} disabled={isSubmitting}>
              Cancel
            </Button>

            {/* Save */}

            <Button
              variant="default"
              onClick={handleSave}
              disabled={isSubmitting || !formData.name || !formData.email}
              iconName={isSubmitting ? 'Loader2' : 'Save'}
              iconPosition="left"
              className={isSubmitting ? 'opacity-70 cursor-not-allowed' : ''}
            >
              {isSubmitting ? 'Saving Changes...' : 'Save Changes'}
            </Button>
          </div>
        </div>
      </div>
    </>
  );
};

export default UserDetailsDrawer;